import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Subject, Observable, Subscription, of } from "rxjs";
import * as moment from "moment";
import { fuseConfig } from "../../fuse-config";
import { isNullOrUndefined } from "util";
import { FuseNavigationService } from "@fuse/components/navigation/navigation.service";
import { VendorBrand } from "../../shared/types/vendor-brand";
import { lprReportNavigationItems } from "../../fuse-config/roles";
// LPR: de lpr-menu (que no importa nada) y NO de lpr-report. Este servicio va en el bundle
// inicial del portal: lo que importe lo descargan todos los usuarios, tengan o no LPR.
import { lprReportMenuVisible, lprSiteStatus } from "../reports/lpr/lpr-menu";
// LPR · "Lecturas de la camara": mismo criterio, helpers sin imports en lpr-reads-menu.
import { lprReadsNavigationItems } from "../../fuse-config/roles";
import { lprCamerasSiteStatus, lprReadsMenuVisible } from "../reports/lpr/lpr-reads-menu";

@Injectable({
    providedIn: "root",
})
export class BuildingService {
    private subject;
    private subjectRadarSetup;
    public building = null;
    public hasconnector = null;
    public haspanel = null;
    public panelDoor = null;
    public panelMembers = null;
    public activeSchedules = [];
    public activeAreas = [];
    public accessLevels: any[];
    public hasAllowRadarSetup: boolean;
    public buildingSettings: any;
    // LPR · lo ultimo que respondio GET lpr/status, y para que sitio. El item de menu del
    // reporte "Control vehicular (LPR)" se muestra solo si `report` es true para el sitio
    // seleccionado; mientras no hay respuesta sigue oculto (loadLprStatus).
    public lprStatus = lprSiteStatus(null, null);
    private lprStatusSubscription: Subscription;
    // LPR · lo ultimo que respondio GET lpr/cameras, y para que sitio. El item "Lecturas de
    // la camara (LPR)" se muestra solo si trajo al menos una camara para el sitio
    // seleccionado; mientras no hay respuesta sigue oculto (loadLprReadsMenu).
    public lprCamerasStatus = lprCamerasSiteStatus(null, null);
    private lprCamerasSubscription: Subscription;

    constructor(private http: HttpClient, private _fuseNavigationService: FuseNavigationService) {
        this.subject = new Subject();
        this.subjectRadarSetup = new Subject();
    }

    subscribe(next, error?, complete?): any {
        return this.subject.subscribe(next, error, complete);
    }

    unsubscribe(next, error?, complete?): any {
        return this.subject.unsubscribe(next, error, complete);
    }
    selectBuilding(building): any {
        // LPR: `building` solo se vacia al cerrar o vencer la sesion. Una consulta de
        // lpr/status que siga pendiente de antes es de la sesion ANTERIOR: se cancela, para
        // que su respuesta no le muestre el item a quien entro despues en el mismo sitio.
        if (!this.building) {
            this.cancelLprStatus();
            this.cancelLprCameras();
        }
        this.building = building;
        this.hasconnector = building.hasconnector;
        this.haspanel = building.haspanel;
        // LPR: el sitio tambien se cambia desde fuera de la pantalla de administracion
        // (Speed Violations tiene su propio selector) y ahi nadie vuelve a preguntar
        // lpr/status: el item del reporte no puede quedarse con lo que se supo de OTRO sitio.
        this.hideLprMenuItemsOfOtherSite();
        this.hideLprReadsMenuItemsOfOtherSite();
        this.subject.next(building);
    }

    setHasAllowedRadarSetup(hasAllowedRadarSetup: boolean): void {
        this.hasAllowRadarSetup = hasAllowedRadarSetup;
        this.subjectRadarSetup.next(hasAllowedRadarSetup);
    }

    subscribeRadarSetup(next, error?, complete?): any {
        return this.subjectRadarSetup.subscribe(next, error, complete);
    }

    getUserRequests(building): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/requestedUsers/" + building);
    }

    approveUserRequest(building, id): Observable<any> {
        return this.http.put(fuseConfig.url + "/buildingAdmin/approveRequestedUser/" + building + "/" + id, {});
    }

    rejectUserRequest(building, id): Observable<any> {
        return this.http.put(fuseConfig.url + "/buildingAdmin/rejectRequestedUser/" + building + "/" + id, {});
    }

    getUserBuildings(): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/userBuildings");
    }

    getUserBuildingById(id): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/userBuildings?filter=buildingid:eq:" + id);
    }

    getBuildingInfo(id): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/buildingInfo/" + id);
    }

    getCountries(): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/countries");
    }

    getTimezones(): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/timezones");
    }

    getCountryStates(id): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/states/" + id);
    }

    getCommunityReport(building, filter): Observable<any> {

        let filterValues = `?filter=unit:like:${filter.unit}`;
        if (filter.rented === true || filter.renter === false) {
            filterValues += `|rented:eq:${filter.rented}`;
        }
        if (filter.resident) {
            filterValues += `&resident=${filter.resident}`;
        }
        filterValues += `&staff=${filter.staff}`;
        // if (filter.resident !== '') {
        //     filter += `|resident:in:${filter.resident}`;
        // }
        return this.http.get(fuseConfig.url + "/buildingAdmin/communityReport/" + building + filterValues);
    }

    getVehicleReport(building, filter): Observable<any> {

        let filterValues = "?filter=";
        let filtersAdded = false;

        if (filter.unit) {
            filterValues += `unit:like:${filter.unit}`;
            filtersAdded = true;
        }

        if (filter.guest) {
            filterValues += (filtersAdded ? "|" : "") + `guest:like:${filter.guest}`;
            filtersAdded = true;
        }

        if (filter.plate) {
            filterValues += (filtersAdded ? "|" : "") + `plate:like:${filter.plate}`;
            filtersAdded = true;
        }

        // if (filter.tag) {
        //     filterValues += `|tag:like:${filter.tag}`;
        // }

        if (filter.cardCodeFrom && filter.cardCodeTo) {
            filterValues += (filtersAdded ? "|" : "") + `cardCode:r:${filter.cardCodeFrom}:${filter.cardCodeTo}`;
            filtersAdded = true;
        } else if (filter.cardCodeFrom) {
            filterValues += (filtersAdded ? "|" : "") + `cardCode:gte:${filter.cardCodeFrom}`;
            filtersAdded = true;
        } else if (filter.cardCodeTo) {
            filterValues += (filtersAdded ? "|" : "") + `cardCode:lte:${filter.cardCodeTo}`;
            filtersAdded = true;
        }

        if (filter.brand) {
            filterValues += (filtersAdded ? "|" : "") + `brand:like:${filter.brand}`;
            filtersAdded = true;
        }
        if (filter.model) {
            filterValues += (filtersAdded ? "|" : "") + `model:like:${filter.model}`;
            filtersAdded = true;
        }
        if (filter.color) {
            filterValues += (filtersAdded ? "|" : "") + `color:like:${filter.color}`;
            filtersAdded = true;
        }
        if (filter.year) {
            filterValues += (filtersAdded ? "|" : "") + `year:eq:${filter.year}`;
            filtersAdded = true;
        }

        filter.issDateFrom = filter.issDateFrom ? moment(filter.issDateFrom).format("YYYY-MM-DD") : undefined;
        filter.issDateTo = filter.issDateTo ? moment(filter.issDateTo).format("YYYY-MM-DD") : undefined;

        if (filter.issDateFrom && filter.issDateTo) {
            filterValues += (filtersAdded ? "|" : "") + `issDate:r:${filter.issDateFrom}:${filter.issDateTo}`;
            filtersAdded = true;
        } else if (filter.issDateFrom) {
            filterValues += (filtersAdded ? "|" : "") + `issDate:gte:${filter.issDateFrom}`;
            filtersAdded = true;
        } else if (filter.issDateTo) {
            filterValues += (filtersAdded ? "|" : "") + `issDate:lte:${filter.issDateTo}`;
            filtersAdded = true;
        }

        // Construir parámetros adicionales solo si tienen valor
        const extraParams = [];
        if (filter.guest) extraParams.push(`guest=${encodeURIComponent(filter.guest)}`);
        if (filter.unit) extraParams.push(`resident=${encodeURIComponent(filter.unit)}`);
        if (filter.residentsOnly !== undefined) extraParams.push(`residentsOnly=${filter.residentsOnly}`);
        if (filter.includeLogs) extraParams.push("includeLogs=true");

        // Concatenar parámetros extra con & si existen       
        const extraQuery = extraParams.length > 0 ? "&" + extraParams.join("&") : "";

        return this.http.get(fuseConfig.url + "/buildingAdmin/vehicleReport/" + building + filterValues + extraQuery);
    }

    getVehicleReportNew(building, filter): Observable<any> {

        let filterValues = `?filter=unit:like:${filter.unit}`;
        // console.log(filter);

        if (filter.guest) {
            filterValues += `|guest:like:${filter.guest}`;
        }

        if (filter.plate) {
            filterValues += `|plate:like:${filter.plate}`;
        }

        // if (filter.tag) {
        //     filterValues += `|tag:like:${filter.tag}`;
        // }

        if (filter.cardCodeFrom && filter.cardCodeTo) {
            filterValues += `|cardCode:r:${filter.cardCodeFrom}:${filter.cardCodeTo}`;
        } else if (filter.cardCodeFrom) {
            filterValues += `|cardCode:gte:${filter.cardCodeFrom}`;
        } else if (filter.cardCodeTo) {
            filterValues += `|cardCode:lte:${filter.cardCodeTo}`;
        }

        if (filter.brand) {
            filterValues += `|brand:like:${filter.brand}`;
        }
        if (filter.model) {
            filterValues += `|model:like:${filter.model}`;
        }
        if (filter.color) {
            filterValues += `|color:like:${filter.color}`;
        }
        if (filter.year) {
            filterValues += `|year:eq:${filter.year}`;
        }
        if (filter.cardFacility) {
            filterValues += `|cardFacility:eq:${filter.cardFacility}`;
        }
        if (filter.cardCode) {
            filterValues += `|cardCode:eq:${filter.cardCode}`;
        }
        if (filter.resident) {
            filterValues += `|residentUnit:like:${filter.resident}`;
        }
        if (filter.door) {
            filterValues += `|door:eq:${filter.door}`;
        }
        if (filter.action) {
            filterValues += `|cardCode:eq:${filter.action}`;
        }
        if (filter.guest_type) {
            filterValues += `|guestType:eq:${filter.guest_type}`;
        }

        const fromHour = moment(filter.fromHour, "HH:mm");
        const toHour = moment(filter.toHour, "HH:mm");
        filter.issDateFrom =
            filter.dateType != "all" && filter.issDateFrom
                ? moment(filter.issDateFrom)
                      .set({
                          hour: fromHour.get("hour"),
                          minute: fromHour.get("minute"),
                      })
                      .format("YYYY-MM-DDTHH:mm:ss")
                : undefined;
        filter.issDateTo =
            filter.dateType != "all" && filter.issDateTo
                ? moment(filter.issDateTo)
                      .set({
                          hour: toHour.get("hour"),
                          minute: toHour.get("minute"),
                      })
                      .format("YYYY-MM-DDTHH:mm:ss")
                : undefined;

        // if (filter.issDateFrom && filter.issDateTo) {
        //     filterValues += `|issDate:r:${filter.issDateFrom}:${filter.issDateTo}`;
        if (filter.issDateFrom) {
            filterValues += `|issDate:gte:${filter.issDateFrom}`;
        }
        if (filter.issDateTo) {
            filterValues += `|issDate:lte:${filter.issDateTo}`;
        }

        // if (filter.resident !== '') {
        //     filter += `|resident:in:${filter.resident}`;
        // }
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/vehicleReport/" +
                building +
                filterValues +
                `&residentsOnly=${filter.residentsOnly}${
                    filter.includeLogs ? "&includeLogs=true" : ""
                }`
        );
    }

    getUnitReport(building, unit): Observable<any> {
        return this.http.get(fuseConfig.url + `/buildingAdmin/unitReport/${building}/${unit}`);
    }

    getResidentDetails(building, resident): Observable<any> {
        // let filterValues = `?filter=resident:like:${filter.resident}|unit:like:${filter.unit}`;
        // if (filter.rented === true || filter.renter === false) {
        //     filterValues += `|rented:eq:${filter.rented}`;
        // }
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/residentDetails/" +
                building +
                "/" +
                resident
        );
    }

    updateBuilding(building, body): Observable<any> {
        const data = { ...body };
        data.img =
            data.img != ""
                ? data.img.includes("data:image/png;base64")
                    ? data.img.replace("data:image/png;base64,", "")
                    : data.img.includes("data:image/jpeg;base64")
                    ? data.img.replace("data:image/jpeg;base64,", "")
                    : undefined
                : undefined;

        return this.http.put(
            fuseConfig.url + "/buildingAdmin/buildingInfo/" + building,
            data
        );
    }

    createBuildingAdmin(building, body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/createAdmin/" + building,
            body
        );
    }

    updateBuildingAdmin(building, body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/updateAdmin/" + building,
            body
        );
    }

    toggleBuildingAdmin(building, admin): Observable<any> {
        const body = {
            email: admin.user,
            name: admin.name,
            lastname: admin.lastname,
            status: admin.status === "A" ? "S" : "A",
            admin: admin.id,
        };
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/updateAdmin/" + building,
            body
        );
    }

    toggleUnitAdmin(unit, admin): Observable<any> {
        const body = {
            email: admin.user,
            name: admin.name,
            lastname: admin.lastname,
            status: admin.status
                ? admin.status === "A"
                    ? "S"
                    : "A"
                : admin.status,
            admin: admin.id,
            phone: admin.phone,
        };
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/BuildingUserUnit/" + unit,
            body
        );
    }

    changeAdminPassword(building, body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/changeAdminPassword/" + building,
            body
        );
    }

    getGates(building): Observable<any> {
        return this.http.get(
            fuseConfig.url + "/buildingAdmin/buildingGate/" + building,
        );
    }

    getGateUsers(building, gate): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/buildingGateUsers/" +
                building +
                "/" +
                gate
        );
    }

    getBuildingSecurity(building): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/buildingSecurityUsers/" + building);
    }

    createGate(building, body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/buildingGate/" + building,
            body
        );
    }

    updateGate(building, body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/buildingGate/" + building,
            body
        );
    }

    deleteGate(gate): Observable<any> {
        return this.http.delete(
            fuseConfig.url + "/buildingAdmin/buildingGate/" + gate
        );
    }

    updateUserSecurity(building, body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/updateSecurity/" + building,
            body
        );
    }

    createUserSecurity(building, body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/createSecurity/" + building,
            body
        );
    }

    assignSecurityGate(building, body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/assignSecurityGate/" + building,
            body
        );
    }

    toggleBuildingSecurity(building, security): Observable<any> {
        const body = {
            email: security.user,
            name: security.name,
            gate: security.gate,
            lastname: security.lastname,
            status: security.status === "A" ? "S" : "A",
            security: security.id,
        };
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/updateSecurity/" + building,
            body
        );
    }

    changeUserPassword(building, body): Observable<any> {
        return this.http.put(
            fuseConfig.url +
                "/buildingAdmin/changeSecurityPassword/" +
                building,
            body
        );
    }

    getTowers(building): Observable<any> {
        return this.http.get(
            fuseConfig.url + "/buildingAdmin/buildingTower/" + building
        );
    }

    createTowers(building, body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/buildingTower/" + building,
            body
        );
    }

    updateTower(building, body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/buildingTower/" + building,
            body
        );
    }

    getUserUnits(building, tower, search): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/buildingUnit/" +
                building +
                "?tower=" +
                tower +
                "&search=" +
                search
        );
    }

    getUnitDetails(building, unit): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/buildingUnit/" +
                building +
                "/" +
                unit
        );
    }

    createUnit(building, body): Observable<any> {
        if (this.building.type !== "T") {
            body.tower = "00000000-0000-0000-0000-000000000000";
        }
        body.tower = body.tower || "00000000-0000-0000-0000-000000000000";
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/buildingUnit/" + building,
            body
        );
    }

    updateUnit(building, body): Observable<any> {
        if (this.building.type !== "T") {
            body.tower = "00000000-0000-0000-0000-000000000000";
        }
        body.tower = body.tower || "00000000-0000-0000-0000-000000000000";
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/buildingUnit/" + building,
            body
        );
    }

    createUserUnit(building, body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/BuildingUserUnit/" + building,
            body
        );
    }

    newOwner(data): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/resetUnit/" + data,
            {}
        );
    }

    // GouTru Connector

    getConnectors(): Observable<any> {
        return this.http.get(
            fuseConfig.url + "/buildingAdmin/goutru_connector"
        );
    }

    getAlive(connector): Observable<any> {
        return this.http.get(
            fuseConfig.url + "/buildingAdmin/alive/" + connector
        );
    }

    getPanelBrands(): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/control_panel_brand?filter=available:eq:true"
        );
    }

    getControlPanels(connector): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/goutru_connector/" +
                connector +
                "/control_panel"
        );
    }

    getControlPanelsByBuilding(building): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/building/" +
                building +
                "/control_panel"
        );
    }

    getPanelsByControlPanel(controlPanel): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/control_panel/" +
                controlPanel +
                "/panels"
        );
    }

    getPanels(connector): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/panels`
        );
    }

    getCameraBrands(): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/camera_brand?filter=available:eq:true"
        );
    }

    getNVRBrands(): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/nvr_brand?filter=available:eq:true"
        );
    }

    getDevices(connector): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/goutru_connector/" +
                connector +
                "/device"
        );
    }

    getDoors(connector): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/goutru_connector/" +
                connector +
                "/door"
        );
    }

    addControlPanel(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/control_panel/",
            body
        );
    }

    addDevice(body): Observable<any> {
        return this.http.post(fuseConfig.url + "/buildingAdmin/device/", body);
    }

    getCameraSnapshot(id, channel, gtc_port): Observable<any> {
        return this.http.get(
            fuseConfig.url2 + `/device/${id}/photo/${channel}/${gtc_port}`
        );
    }

    getPhoto(device): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/device/photo/",
            { device: device }
        );
    }

    getPreview(camera_data): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/device/preview/",
            camera_data
        );
    }

    syncDoors(connector): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/goutru_connector/" +
                connector +
                "/door/sync"
        );
    }

    openDoor(door): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/door/open",
            { door: door }
        );
    }

    actionDoor(door: string, action: string, extended: boolean = false): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/door/action",
            { door, action, extended }
        );
    }

    getLevels(connector): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/goutru_connector/" +
                connector +
                "/access_level"
        );
    }

    syncLevels(connector): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/goutru_connector/" +
                connector +
                "/access_level/sync"
        );
    }

    getBuildingDoors(building): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/door?filter=building:eq:" +
                building
        );
    }

    getDoorGates(door): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/door/" + door);
    }

    getGateDoors(gate): Observable<any> {
        return this.http.get(
            fuseConfig.url + "/buildingAdmin/gateDoors/" + gate
        );
    }

    getBuildingCameras(building): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/device?filter=is_nvr:eq:false|building:eq:" +
                building
        );
    }

    getCameraDoors(camera): Observable<any> {
        return this.http.get(
            fuseConfig.url + "/buildingAdmin/camera/" + camera
        );
    }

    assignGateDoor(building, gate, door, principal): Observable<any> {
        const body = {
            building,
            gate,
            door,
            principal,
        };
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/assignGateDoor",
            body
        );
    }

    unassignGateDoor(building, gate, door): Observable<any> {
        const body = {
            building,
            gate,
            door,
        };
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/unassignGateDoor",
            body
        );
    }

    setDoorDevices(data): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/door/devices",
            data
        );
    }

    setConnectorBuilding(data): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/goutru_connector/building",
            data
        );
    }

    updateDevice(body): Observable<any> {
        return this.http.put(fuseConfig.url + "/buildingAdmin/device/", body);
    }

    deleteDevice(device): Observable<any> {
        return this.http.delete(
            fuseConfig.url + "/buildingAdmin/device/" + device
        );
    }

    // LPR: la seccion LPR de una camara va por rutas propias; el CRUD de device
    // de arriba no cambia. Un API sin LPR responde 404 (HTML, sin message) y uno
    // que lo niega 403: quien llama decide no pintar nada.

    getDeviceLpr(device): Observable<any> {
        return this.http.get(
            fuseConfig.url + "/buildingAdmin/device/" + device + "/lpr"
        );
    }

    updateDeviceLpr(device, body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/device/" + device + "/lpr",
            body
        );
    }

    getConnectorLpr(connector): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/goutru_connector/" +
                connector +
                "/lpr"
        );
    }

    // LPR: ¿el sitio tiene el reporte "Control vehicular (LPR)"? Lo decide el API,
    // con el mismo permiso que exige el reporte. 403 = la sesion no es manager de
    // ese sitio; 404 = el API no tiene LPR. Lo consume loadLprStatus().
    getLprStatus(building): Observable<any> {
        return this.http.get(
            fuseConfig.url + "/buildingAdmin/lpr/status/" + building
        );
    }

    // LPR: ¿el sitio tiene camaras LPR para "Lecturas de la camara"? Mismo permiso que la
    // pantalla (managers del sitio). Lo consume loadLprReadsMenu().
    getLprCameras(building): Observable<any> {
        return this.http.get(
            fuseConfig.url + "/buildingAdmin/lpr/cameras/" + building
        );
    }

    updateControlPanel(body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/control_panel/",
            body
        );
    }

    updateControlPanelInfo(body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/goutruAdmin/control_panel/info",
            body
        );
    }

    updateControlPanelStatus(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/goutruAdmin/control_panel/update/status",
            body
        );
    }

    createUpdateBuildingSettings(body): Observable<any> {
        if (body.access_level && Array.isArray(body.access_level)) {
            body.access_level = body.access_level.map(access => ({
                access_level_uuid: access.access_level_uuid,
                checked: access.checked
            }));
        }

        body.event_img =
            body.event_img != ""
                ? body.event_img.includes("data:image/png;base64")
                    ? body.event_img.replace("data:image/png;base64,", "")
                    : body.event_img.includes("data:image/jpeg;base64")
                    ? body.event_img.replace("data:image/jpeg;base64,", "")
                    : undefined
                : undefined;

        return this.http.post(
            fuseConfig.url + "/buildingAdmin/settings",
            body
        );
    }

    createUpdateAdvancedBuildingSettings(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/settings/advanced",
            body
        );
    }

    createUpdateKioskSmartSettings(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/kioskSmart",
            body
        );
    }

    createUpdateKioskSmartIPCallerSettings(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/kioskSmart/postConnect",
            body
        );
    }

    getBuildingSettings(id): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/settings/" + id);
    }

    getMasterCodeInfo(id): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/masterCodeInfo/" + id);
    }

    requestJoinMaster(buildingId: string, masterBuildingId: string): Observable<any> {
        return this.http.post(
            `${fuseConfig.url}/buildingAdmin/masterAssociate/request/${buildingId}/${masterBuildingId}`,
            {}
        );
    }

    respondToAssociationRequest(response: string, associationId: string): Observable<any> {   
        return this.http.put(
            `${fuseConfig.url}/buildingAdmin/masterAssociate/${response}/${associationId}`,
            {},
        );
    }

    /**
     * RPGS · Resident PIN repetidos que NACERIAN al aprobar una solicitud de
     * asociacion. `associationId` es el mismo uuid de masterAssociate/approve.
     * Responde { status: 200, collisions, residents }: `collisions` = cuantos PIN
     * distintos del sitio que pide asociarse ya existen en la red a la que
     * entraria; `residents` = cuantas personas quedan involucradas. Solo cuenta:
     * no devuelve ningun PIN.
     *
     * Es un AVISO previo, no un bloqueo: quien lo consume aprueba igual si esto
     * falla (un API viejo no tiene la ruta).
     */
    getAssociationPincodeCollisions(associationId: string): Observable<any> {
        return this.http.get(
            `${fuseConfig.url}/buildingAdmin/masterAssociate/pincodeCollisions/${associationId}`
        );
    }

    disassociateMaster(associationId: string): Observable<any> {    
        return this.http.delete(
            `${fuseConfig.url}/buildingAdmin/masterAssociate/disassociate/${associationId}`
        );
    }

    getSmartKioskSettings(id): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/kioskSmart/" + id);
    }

    getSmartKioskStaff(id): Observable<any> {
        return this.http.get(fuseConfig.url + `/buildingAdmin/kioskSmart/${id}/staff`);
    }


    getBuildingDealer(id): Observable<any> {
        return this.http.get(fuseConfig.url + "/buildingAdmin/dealer/" + id);
    }

    testMailSettings(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/settings/mail/test",
            body
        );
    }

    updateMailSettings(body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/settings/mail/update",
            body
        );
    }

    getBuildingNotes(building): Observable<any> {
        return this.http.get(
            fuseConfig.url + "/buildingAdmin/getBuildingNotes/" + building
        );
    }

    updateBuildingNotes(body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/updateBuildingNotes",
            body
        );
    }

    resetControlPanel(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/control_panel/reset",
            body
        );
    }

    resetControlPanelTaskStatus(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/control_panel/reset/status",
            body
        );
    }

    syncControlPanel(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/control_panel/sync",
            body
        );
    }

    syncControlPanelStatus(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/control_panel/sync/status",
            body
        );
    }

    refreshControlPanel(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/control_panel/refresh",
            body
        );
    }

    refreshControlPanelStatus(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/control_panel/refresh/status",
            body
        );
    }

    syncCards(data): Observable<any> {
        const body = { building: data };
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/card/sync",
            body
        );
    }

    getGuests(unit, status, resident): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/getUnitGuests/" +
                unit +
                "/" +
                status +
                "/" +
                resident
        );
    }

    getResidents(unit): Observable<any> {
        return this.http.get(
            fuseConfig.url + "/buildingAdmin/getUnitResidents/" + unit
        );
    }

    getVehicles(unit, resident): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/getUnitVehicles/" +
                unit +
                "/" +
                resident
        );
    }

    getUsers(building, filter): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/admin/users/" +
                building +
                `?filter=name:like:${filter}`
        );
    }

    getUserResidents(user): Observable<any> {
        return this.http.get(fuseConfig.url + "/admin/userResidents/" + user);
    }

    toggleUserStatus(email): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/admin/toggleUserStatus",
            { email }
        );
    }

    deleteResidentUser(resident): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/admin/deleteResidentUser",
            { resident }
        );
    }

    softDeleteResident(resident_id): Observable<any> {
        return this.http.delete(
            fuseConfig.url + "/buildingAdmin/softDeleteResident/" + resident_id
        );
    }

    //faked
    updateDealer(body): Observable<any> {
        const token = localStorage.getItem("token")
            ? localStorage.getItem("token")
            : sessionStorage.getItem("token");
        const headers = new HttpHeaders().set("authorization", token);
        headers.append("Content-Type", "application/json");
        // return this.http.delete(fuseConfig.url + '/buildingAdmin/softDeleteResident/', { headers: headers, withCredentials: true });
        return of({ status: 200, message: "" });
    }

    resetDemoCommunity(): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingCreator/demo",
            {}
        );
    }
    getAreas(connector, filter?): Observable<any> {
        let f = filter ? "?filter=valid:eq:true" : "";
        return this.http.get(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/areas` +
                f
        );
    }

    updateArea(connector, data, area) {
        return this.http.put(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/areas/${area}`,
            data
        );
    }

    createArea(connector, data) {
        return this.http.post(
            fuseConfig.url + `/goutruAdmin/goutru_connector/${connector}/area`,
            data
        );
    }

    deleteArea(connector, area) {
        return this.http.delete(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/area/${area}`
        );
    }

    getHolidays(connector, filter?) {
        let f = filter ? "?filter=valid:eq:true" : "";
        return this.http.get(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/holidays` +
                f
        );
    }

    syncHolidays(connector) {
        return this.http.post(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/holidays/sync`,
            {}
        );
    }

    syncSchedules(connector) {
        return this.http.post(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/schedules/sync`,
            {}
        );
    }

    syncAreas(connector) {
        return this.http.post(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/areas/sync`,
            {}
        );
    }

    syncMembers(connector) {
        return this.http.post(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/members/sync`,
            {}
        );
    }

    syncAccessLevels(connector) {
        return this.http.get(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/access_level/sync`
        );
    }

    syncPanelDoors(connector) {
        return this.http.get(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/door/sync`
        );
    }

    createHoliday(data, gtc) {
        return this.http.post(
            fuseConfig.url + `/goutruAdmin/goutru_connector/${gtc}/holidays`,
            data
        );
    }

    updateHoliday(data, gtc) {
        return this.http.put(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${gtc}/holidays/${data.holiday}`,
            data
        );
    }

    deleteHoliday(connector, holiday) {
        return this.http.delete(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/holidays/${holiday}`
        );
    }

    getPanelDoors(gtc) {
        return this.http.get(
            fuseConfig.url + `/goutruAdmin/goutru_connector/${gtc}/door`
        );
    }

    getDoorsStatus(gtc): Observable<any> {
        return this.http.get(
            fuseConfig.url + `/goutruAdmin/goutru_connector/${gtc}/door/status`
        );
    }

    sendDoorAction(action, door) {
        const data = {
            door: door,
            action: action,
            extended: false,
        };

        if (action === "grantextend") {
            data.action = "grantaccess";
            data.extended = true;
        }

        return this.http.post(
            fuseConfig.url + `/goutruAdmin/door/action`,
            data
        );
    }

    updatePanelDoor(connector, data, door) {
        return this.http.put(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/door/${door}`,
            data
        );
    }

    getAccessLevel(gtc, filter?): Observable<any> {
        let f = filter ? "?filter=valid:eq:true" : "";
        return this.http.get(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${gtc}/access_level${f}`
        );
    }

    getMasterAccessLevel(associatedBuilding, filter?): Observable<any> {
        let f = filter ? "?filter=valid:eq:true" : "";
        return this.http.get(
            fuseConfig.url +
                `/masterAssociated/shared/${associatedBuilding}/access_level`,
        );
    }

    createAccessLevel(connector, data) {
        return this.http.post(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/access_level`,
            data
        );
    }

    updateAccessLevel(connector, data, al) {
        return this.http.put(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/access_level/${al}`,
            data
        );
    }

    deleteAccessLevel(connector, AL) {
        return this.http.delete(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/access_level/${AL}`
        );
    }

    getSchedules(gtc, filter?) {
        let f = filter ? "?filter=valid:eq:true" : "";
        return this.http.get(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${gtc}/schedules` +
                f
        );
    }

    createSchedule(connector, data) {
        return this.http.post(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/schedules`,
            data
        );
    }

    updateSchedule(connector, data, al) {
        return this.http.put(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/schedules/${al}`,
            data
        );
    }

    deleteSchedule(connector, schedule) {
        return this.http.delete(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/schedules/${schedule}`
        );
    }

    downloadImportDataTemplate(): Observable<any> {
        const headers = new HttpHeaders().set("Content-Type", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
        return this.http.get(fuseConfig.url + `/buildingAdmin/template`, {
            headers: headers,
            responseType: "blob",
        });
    }

    validateImportData(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + `/buildingAdmin/validate`,
            body
        );
    }

    importData(log): Observable<any> {
        return this.http.post(
            fuseConfig.url + `/buildingAdmin/import`,
            { import_log: log }
        );
    }

    importDataStatus(building, log): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                `/buildingAdmin/import/status/${building}/${log}?filter=cards_import:eq:false`
        );
    }

    cancelImportData(log): Observable<any> {
        return this.http.delete(
            fuseConfig.url + `/buildingAdmin/import/${log}`
        );
    }

    getMembers(gtc, search?, filter?): Observable<any> {
        if (isNullOrUndefined(search)) {
            search = "";
        }

        return this.http.get(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${gtc}/members/${search || ""}?${
                    filter || ""
                }`
        );
    }

    getCardsMember(gtc: any, id: string): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${gtc}/member/${id}/cards`
        );
    }

    createMember(connector, data): Observable<any> {
        return this.http.post(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/members`,
            data
        );
    }

    updateMember(connector, data, al) {
        return this.http.put(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/members/${al}`,
            data
        );
    }

    deleteMember(connector, schedule) {
        return this.http.delete(
            fuseConfig.url +
                `/goutruAdmin/goutru_connector/${connector}/members/${schedule}`
        );
    }

    createMemberUser(building, body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/createMemberUser/" + building,
            body
        );
    }

    getEvents(gtc, filter?, object?): Observable<any> {
        if (isNullOrUndefined(filter.type)) {
            filter.type = "all";
        }

        // El API filtra evtdate contra panel_event.date_time, que es la hora local del
        // edificio (la misma que se muestra en la tabla). La fecha y hora elegidas ya están
        // en esa hora local, así que viajan tal cual, sin convertir de zona horaria.
        // - Segundos: la hora final cubre el minuto completo (HH:mm:59); sin ellos quedaban
        //   fuera los eventos entre HH:mm:01 y HH:mm:59.
        // - Sufijo Z: el API (QueryFilterSort) convierte el valor a Date y lo serializa en
        //   UTC; sin zona lo interpretaría con la zona horaria del servidor y correría el
        //   rango. Con Z el valor llega intacto, y Postgres ignora la Z al compararlo con
        //   date_time (timestamp sin zona). Event Report usa el mismo criterio.
        const wallTime = (date, hour: string, seconds: string) =>
            `${moment(date).format("YYYY-MM-DD")}T${/^\d{2}:\d{2}$/.test(hour) ? `${hour}:${seconds}` : hour}Z`;
        filter.fromDate = filter.fromDate ? wallTime(filter.fromDate, filter.fromHour, "00") : undefined;
        filter.toDate = filter.toDate ? wallTime(filter.toDate, filter.toHour, "59") : undefined;

        // Construir partes del filtro sólo con contenido real (evita ?filter= vacío).
        const filterParts: string[] = [];
        if (filter.fromDate) {
            filterParts.push(`evtdate:gte:${filter.fromDate}`);
        }
        if (filter.toDate) {
            filterParts.push(`evtdate:lte:${filter.toDate}`);
        }
        if (filter.description) {
            filterParts.push(`description:like:${filter.description}`);
        }

        const filterValues = filterParts.length
            ? `?filter=|${filterParts.join("|")}`
            : "";

        const url =
            fuseConfig.url +
            `/goutruAdmin/goutru_connector/${gtc}/events/${filter.type || "all"}${
                object ? `/${object}` : ""
            }${filterValues}`;

        return this.http.get(url);
    }

    getUserDepartments(building, search): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/departments/" +
                building +
                "?search=" +
                search
        );
    }

    getDepartmentDetails(building, department): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/departments/" +
                building +
                "/" +
                department
        );
    }

    createDepartment(building, body): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/buildingAdmin/departments/" + building,
            body
        );
    }

    updateDepartment(building, body): Observable<any> {
        return this.http.put(
            fuseConfig.url + "/buildingAdmin/departments/" + building,
            body
        );
    }

    createCardMember(body: any): Observable<any> {
        return this.http.post(
            fuseConfig.url + "/goutruAdmin/cardmember",
            body
        );
    }

    getSpeedInfo(id_building: string, id_unit_house: string): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                `/building/${id_building}/unit_house/${id_unit_house}/sltickets`
        );
    }

    postPrint(id_building: string, id_ticket: string): Observable<any> {
        return this.http.post(
            fuseConfig.url +
                `/building/${id_building}/sltickets/${id_ticket}/print`,
            "",
            {
                responseType: "arraybuffer",
            }
        );
    }

    getTicketInformation(
        id_building: string,
        id_ticket: string
    ): Observable<any> {
        return this.http.get(fuseConfig.url + `/building/${id_building}/sltickets/${id_ticket}`);
    }

    getTicketStatus(id_building: string): Observable<any> {
        return this.http.get(
            fuseConfig.url + `/building/${id_building}/tickets-status`
        );
    }

    postSlchgstatus(id_building: string, id_unit_house: string, body: any): Observable<any> {
        return this.http.post(
            fuseConfig.url + `/building/${id_building}/unit_house/${id_unit_house}/slchgstatus`,
            body
        );
    }

    getScanImage(id_building: string, id_photo: string): Observable<any> {
        return this.http.get(
            fuseConfig.url + `/buildingAdmin/${id_building}/scanPhoto/${id_photo}`
        );
    }

    getResidentsByBuilding(building: string, resident?: string, kiosk = ""): Observable<any> {
        return this.http.get( fuseConfig.url + '/buildingAdmin/residents/' + building + '?' + `${resident? `filter=id:eq:${resident}`:''}` + (kiosk ? `&kiosk=${kiosk}` : ""));
    }

    getResidentsOnDirectory(building: string, resident?:string, kiosk = ""): Observable<any> {
        return this.http.get(
            fuseConfig.url +
                "/buildingAdmin/residents/kioskSmart/" +
                building + '?' +
                `${resident ? `filter=id:eq:${resident}` : ""}` +
                (kiosk ? `&kiosk=${kiosk}` : "")
        );
    }

    putSetPincodes(body: any, remove: boolean, manual?:boolean): Observable<any> {
        return this.http.put(
            fuseConfig.url + `/buildingAdmin/residents/kioskSmart/setPincode?removeAll=${remove}&manual=${manual? manual:false}`,
            body
        );
    }

    postResidents(body: any): Observable<any> {
        return this.http.post(
            fuseConfig.url + `/buildingAdmin/residents/kioskSmart/`,
            body
        );
    }
    
    deleteResidents(body: any): Observable<any> {
        return this.http.post(
            fuseConfig.url + `/buildingAdmin/residents/kioskSmart/delete`,
            body
        );
    }

    updateResidentOnSmartKiosk(body): Observable<any> {
        return this.http.put(
            fuseConfig.url + `/buildingAdmin/residents/kioskSmart/`,
            body
        );
    }

    updatePhotoResidentOnSmartKiosk(body): Observable<any> {
        return this.http.post(
            fuseConfig.url + `/buildingAdmin/residents/kioskSmart/photo`,
            body
        );
    }

    getPhotoResidents(building: string, resident:string): Observable<any> {
        return this.http.get( fuseConfig.url + `/buildingAdmin/residents/kioskSmart/photo/${resident}/${building}`);
    }

    deletePhotoResidents(building: string, resident:string): Observable<any> {
        return this.http.delete( fuseConfig.url + `/buildingAdmin/residents/kioskSmart/photo/${resident}/${building}`);
    }


    getResidentList(building: string, status: string, filter?: string): Observable<any> {
        const query = `${status ? `status=${status}` : ""}${filter ? `&filter=${filter}` : ""}`;
        return this.http.get(
            fuseConfig.url +
                `/buildingAdmin/${building}/residents?${query}`
        );
    }

    resendEmailMultiple(residents: { resident: string; email: string }[]): Observable<any> {
        return this.http.put(
            fuseConfig.url + `/buildingAdmin/resendEmail/multiple`,
            {residents}
        );
    }

    postBuildingAdminSettingsMailUnitData(body: any): Observable<any> {
        return this.http.post(fuseConfig.url + `/buildingAdmin/settings/mail/unitData`, body);
    }

    /**
     * La lista de buildings (y su vendor_brand) solo se carga al iniciar la app;
     * al vincular/editar un panel hay que reflejar el vendor en memoria y
     * re-evaluar el menú lateral sin esperar un reload completo.
     * `building` (opcional): uuid del site del panel guardado — si no coincide
     * con el site seleccionado no se toca nada.
     */
    applyVendorBrand(brand: string, building?: string): void {
        if (!this.building) { return; }
        if (building && this.building.value !== building) { return; }
        this.building.vendorBrand = brand;
        if (this.building.buildingSettings) {
            this.toggleFeatureMenuItems();
        }
    }

    toggleFeatureMenuItems() {
        const approvedIndex = this.building.buildingSettings.ma_join_to_request_send.findIndex(item => item.join_approved === true);
        const buildingSettings = this.building.buildingSettings;
        const isAssociate = buildingSettings.adv_master_association === true && buildingSettings.adv_is_master === false && approvedIndex !== -1;
        // PDK buildings have no goutru_connector but DO have panels — don't treat them as "without panel"
        const isPdk = this.building.vendorBrand === VendorBrand.PDK;
        const isAssociateWithoutPanel = isAssociate && this.building.goutru_connector === null && !isPdk;
        const associateCanCreateCards = isAssociate && this.building.buildingSettings.ma_join_to_request_send[approvedIndex].master_allow_add_cards === true

        const menuFeaturesMap = {
            speed_violations: buildingSettings.adv_allow_radar_setup,
            admbuild_kiosks_list: buildingSettings.adv_allow_smart_kiosk_setup,
            qr_readers: buildingSettings.adv_allow_qr_readers_setup,
            // El mismo menu QR Readers en el grupo dealer (id propio, ver roles.ts).
            dealer_qr_readers: buildingSettings.adv_allow_qr_readers_setup,
            smart_locks: buildingSettings.adv_allow_smart_lock_integration,
            compliance_module: buildingSettings.adv_allow_compliance_module,
            // Entre comillas porque el id del item usa guiones y no guion bajo
            // como el resto (ver roles.ts).
            'access-code-types': buildingSettings.access_code_enabled,
            admbuild_monitor: buildingSettings.adv_allow_panel_setup && !isAssociateWithoutPanel,
            // Access Monitoring tambien lista los eventos de los lectores QR: un sitio
            // solo con lectores QR (sin panel) tambien lo usa.
            admbuild_report_access_mon: buildingSettings.adv_allow_panel_setup || buildingSettings.adv_allow_qr_readers_setup,
            admbuild_tags: buildingSettings.adv_allow_panel_setup,
            control_panel: buildingSettings.adv_allow_panel_setup,
            incident_report: buildingSettings.enable_incident_report,
            dealer_monitor: !isAssociateWithoutPanel,
            dealertech_monitor: !isAssociateWithoutPanel,
            master_access: isAssociate,
            doors: !isAssociateWithoutPanel,
            ma_access_level: associateCanCreateCards,
        };
        Object.keys(menuFeaturesMap).forEach((menuFeature) => {
            this._fuseNavigationService.updateNavigationItem(menuFeature, {
                hidden: !menuFeaturesMap[menuFeature],
            });
        });

        // Vendor-specific menu items
        const hasPanelSetup = buildingSettings.adv_allow_panel_setup;
        // Shared items: visible for all vendors with panel setup
        const sharedItems = ['cards-cdvi'];
        sharedItems.forEach((item) => {
            this._fuseNavigationService.updateNavigationItem(item, { hidden: !hasPanelSetup });
        });

        // CDVI-only items: hide when PDK building
        const cdviOnlyItems = ['panel-doors', 'areas', 'access-level', 'holidays', 'schedules', 'panel-events'];
        cdviOnlyItems.forEach((item) => {
            this._fuseNavigationService.updateNavigationItem(item, { hidden: !hasPanelSetup || isPdk });
        });

        // PDK-only items: hide when CDVI building
        const pdkOnlyItems = ['pdk-doors', 'pdk-access-levels', 'pdk-areas'];
        pdkOnlyItems.forEach((item) => {
            this._fuseNavigationService.updateNavigationItem(item, { hidden: !hasPanelSetup || !isPdk });
        });

        // LPR: el item del reporte "Control vehicular (LPR)" no sale de buildingSettings
        // sino de GET lpr/status (loadLprStatus). Aqui solo se vuelve a aplicar lo ultimo
        // que respondio para este sitio.
        this.toggleLprMenuItems();
        this.toggleLprReadsMenuItems();

    }

    /**
     * LPR · pregunta al API si el sitio seleccionado tiene el reporte "Control vehicular
     * (LPR)" y muestra u oculta su item de menu. Se llama al seleccionar un sitio,
     * despues de toggleFeatureMenuItems().
     *
     * El item arranca oculto y solo se muestra con un 200 que diga `report: true` para
     * ESTE sitio. Con cualquier otra respuesta -403 porque la sesion no es manager del
     * sitio, 404 porque el API no tiene LPR, sin red- queda oculto y NO se avisa: esta
     * llamada la hace el menu, no el usuario.
     */
    loadLprStatus(): void {
        const building = this.building ? this.building.value : null;
        // Lo que se sabia era de otro sitio (o de otra sesion): oculto hasta que responda.
        this.lprStatus = lprSiteStatus(building, null);
        this.toggleLprMenuItems();
        // Solo cuenta la ultima consulta: una respuesta atrasada (de otro sitio, o de la
        // sesion anterior en esta misma pestaña) no puede mostrar el item.
        this.cancelLprStatus();
        if (!building) {
            return;
        }
        this.lprStatusSubscription = this.getLprStatus(building).subscribe(res => {
            // Si mientras tanto se eligio otro sitio, esta respuesta ya no aplica.
            if (this.building && this.building.value === building) {
                this.lprStatus = lprSiteStatus(building, res);
                this.toggleLprMenuItems();
            }
        }, err => {
            // Sigue oculto, sin aviso.
        });
    }

    toggleLprMenuItems(): void {
        const hidden = !lprReportMenuVisible(this.lprStatus, this.building ? this.building.value : null);
        lprReportNavigationItems.forEach((item) => {
            this._fuseNavigationService.updateNavigationItem(item, { hidden: hidden });
        });
    }

    /** LPR · descarta la consulta de lpr/status que siga pendiente: su respuesta ya no cuenta. */
    private cancelLprStatus(): void {
        if (this.lprStatusSubscription) {
            this.lprStatusSubscription.unsubscribe();
            this.lprStatusSubscription = null;
        }
    }

    /**
     * LPR · al cambiar de sitio por CUALQUIER camino (selectBuilding), oculta el item del
     * reporte si lo ultimo que respondio lpr/status no es de ese sitio.
     *
     * Solo OCULTA. Mostrarlo sigue siendo cosa de loadLprStatus(), que pregunta con la
     * sesion actual: volver a aplicar aqui el estado completo le mostraria el item a
     * quien inicie sesion despues de un administrador en la misma pestaña (el guardia
     * tambien pasa por selectBuilding y `lprStatus` sobrevive al cambio de sesion).
     */
    private hideLprMenuItemsOfOtherSite(): void {
        // El item solo pudo quedar visible si lo ultimo que se supo decia `report: true`:
        // en cualquier otro caso (todo sitio sin LPR) no hay nada que ocultar y el menu
        // no se toca.
        if (this.lprStatus.report === true
            && !lprReportMenuVisible(this.lprStatus, this.building ? this.building.value : null)) {
            lprReportNavigationItems.forEach((item) => {
                this._fuseNavigationService.updateNavigationItem(item, { hidden: true });
            });
        }
    }

    /**
     * LPR · pregunta al API si el sitio seleccionado tiene camaras LPR para "Lecturas de la
     * camara (LPR)" y muestra u oculta su item de menu. Mismo ciclo que loadLprStatus(): el
     * item arranca oculto, solo se muestra con un 200 que traiga al menos una camara para
     * ESTE sitio, y cualquier otra respuesta (403, 404, sin red) lo deja oculto sin aviso.
     * Una respuesta de otro sitio, o de la sesion anterior, se descarta.
     */
    loadLprReadsMenu(): void {
        const building = this.building ? this.building.value : null;
        this.lprCamerasStatus = lprCamerasSiteStatus(building, null);
        this.toggleLprReadsMenuItems();
        this.cancelLprCameras();
        if (!building) {
            return;
        }
        this.lprCamerasSubscription = this.getLprCameras(building).subscribe(res => {
            if (this.building && this.building.value === building) {
                this.lprCamerasStatus = lprCamerasSiteStatus(building, res);
                this.toggleLprReadsMenuItems();
            }
        }, err => {
            // Sigue oculto, sin aviso.
        });
    }

    toggleLprReadsMenuItems(): void {
        const hidden = !lprReadsMenuVisible(this.lprCamerasStatus, this.building ? this.building.value : null);
        lprReadsNavigationItems.forEach((item) => {
            this._fuseNavigationService.updateNavigationItem(item, { hidden: hidden });
        });
    }

    /** LPR · descarta la consulta de lpr/cameras del menu que siga pendiente. */
    private cancelLprCameras(): void {
        if (this.lprCamerasSubscription) {
            this.lprCamerasSubscription.unsubscribe();
            this.lprCamerasSubscription = null;
        }
    }

    /**
     * LPR · al cambiar de sitio por cualquier camino, oculta "Lecturas de la camara" si lo
     * ultimo que respondio lpr/cameras no es de ese sitio. Solo OCULTA (mismo motivo que
     * hideLprMenuItemsOfOtherSite).
     */
    private hideLprReadsMenuItemsOfOtherSite(): void {
        if (this.lprCamerasStatus.cameras === true
            && !lprReadsMenuVisible(this.lprCamerasStatus, this.building ? this.building.value : null)) {
            lprReadsNavigationItems.forEach((item) => {
                this._fuseNavigationService.updateNavigationItem(item, { hidden: true });
            });
        }
    }

    getResidentAvailableDirectories(building: string, resident: string): Observable<{directories: any}> {
        return this.http.get(
            `${fuseConfig.url}/buildingAdmin/${building}/residents/${resident}/directories`
        ) as Observable<{ directories: [] }>;
    }

    getGuestTypes(): Observable<any> {
        return this.http.get(fuseConfig.url + '/shared/guest_type');
    }

    // ===== Access Codes (S7) =====
    // CRUD de tipos per-building
    getAccessCodeTypes(building: string): Observable<any> {
        return this.http.get(
            `${fuseConfig.url}/buildingAdmin/accessCodeTypes/${building}`
        );
    }

    createAccessCodeType(building: string, data: any): Observable<any> {
        return this.http.post(
            `${fuseConfig.url}/buildingAdmin/accessCodeTypes/${building}`,
            data
        );
    }

    updateAccessCodeType(building: string, type: string, data: any): Observable<any> {
        return this.http.put(
            `${fuseConfig.url}/buildingAdmin/accessCodeTypes/${building}/${type}`,
            data
        );
    }

    deleteAccessCodeType(building: string, type: string): Observable<any> {
        return this.http.delete(
            `${fuseConfig.url}/buildingAdmin/accessCodeTypes/${building}/${type}`
        );
    }

    // Listado admin de access codes por unit (para tab Access Codes en Unit view)
    getUnitAccessCodes(unit: string, filters?: {
        type?: string;
        mode?: string;
        status?: string;
        from_date?: string;
        to_date?: string;
        description?: string;
    }): Observable<any> {
        const qs = filters
            ? Object.entries(filters)
                .filter(([, v]) => v !== undefined && v !== null && v !== '')
                .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
                .join('&')
            : '';
        return this.http.get(
            `${fuseConfig.url}/buildingAdmin/units/${unit}/accessCodes${qs ? '?' + qs : ''}`
        );
    }

    // Detalle admin de un access code + historial completo de usos
    getAccessCodeAdminDetails(building: string, id: string): Observable<any> {
        return this.http.get(
            `${fuseConfig.url}/buildingAdmin/accessCodes/${building}/${id}`
        );
    }

    // --- PDK building-based endpoints ---

    getLevelsByBuilding(building: string): Observable<any> {
        return this.http.get(fuseConfig.url + `/buildingAdmin/building/${building}/access_level`);
    }

    createLevelByBuilding(building: string, data: any): Observable<any> {
        return this.http.post(fuseConfig.url + '/buildingAdmin/building/' + building + '/access_level', data);
    }

    updateLevelByBuilding(building: string, accessLevel: string, data: any): Observable<any> {
        return this.http.put(fuseConfig.url + '/buildingAdmin/building/' + building + '/access_level/' + accessLevel, data);
    }

    deleteLevelByBuilding(building: string, accessLevel: string): Observable<any> {
        return this.http.delete(fuseConfig.url + '/buildingAdmin/building/' + building + '/access_level/' + accessLevel);
    }

    getRulesByGroup(building: string, accessLevel: string): Observable<any> {
        return this.http.get(fuseConfig.url + '/buildingAdmin/building/' + building + '/access_level/' + accessLevel + '/rules');
    }

    createRuleByGroup(building: string, accessLevel: string, data: any): Observable<any> {
        return this.http.post(fuseConfig.url + '/buildingAdmin/building/' + building + '/access_level/' + accessLevel + '/rules', data);
    }

    updateRuleByGroup(building: string, accessLevel: string, ruleId: string, data: any): Observable<any> {
        return this.http.put(fuseConfig.url + '/buildingAdmin/building/' + building + '/access_level/' + accessLevel + '/rules/' + ruleId, data);
    }

    deleteRuleByGroup(building: string, accessLevel: string, ruleId: string): Observable<any> {
        return this.http.delete(fuseConfig.url + '/buildingAdmin/building/' + building + '/access_level/' + accessLevel + '/rules/' + ruleId);
    }

    getAreasByBuilding(building: string): Observable<any> {
        return this.http.get(fuseConfig.url + `/buildingAdmin/building/${building}/area`);
    }

    getDoorsStatusByBuilding(building: string): Observable<any> {
        return this.http.get(fuseConfig.url + `/buildingAdmin/building/${building}/door/status`);
    }

    getEventHistoryByBuilding(building: string, params: any = {}): Observable<any> {
        let query = '?period=' + encodeURIComponent(params.period || 'today');
        if (params.from) { query += '&from=' + encodeURIComponent(params.from); }
        if (params.to) { query += '&to=' + encodeURIComponent(params.to); }
        if (params.device) { query += '&device=' + encodeURIComponent(params.device); }
        if (params.holder) { query += '&holder=' + encodeURIComponent(params.holder); }
        if (params.credential) { query += '&credential=' + encodeURIComponent(params.credential); }
        return this.http.get(fuseConfig.url + `/buildingAdmin/building/${building}/events` + query);
    }

    getCardsByBuilding(building: string, search: string = ''): Observable<any> {
        return this.http.get(fuseConfig.url + '/buildingAdmin/card/' + encodeURIComponent(search) + '?filter=building:eq:' + building);
    }

}